/**
 * event-bus.ts —— 全局事件总线
 *
 * 设计意图：模块之间只通过事件通信，不互相持有引用。
 * 数据流始终是单向的：
 *   用户操作 / IL 指令 → Repo 写库 → 广播事件 → 组件订阅更新
 * UI 永远不直接改数据，只发意图。
 */

import type { AppEventMap } from '@/types/events';

type Handler<T> = (payload: T) => void;

/** 取消订阅函数，订阅时返回，务必在组件卸载时调用 */
export type Unsubscribe = () => void;

class EventBus {
  /** 事件名 → 处理函数集合。用 Set 避免同一函数被重复注册导致多次触发 */
  private handlers = new Map<string, Set<Handler<never>>>();

  /**
   * 订阅事件。返回取消订阅函数。
   * 推荐用法（配合 LxElement 基类，可自动解绑）：
   *   this.on('theme:changed', (p) => this.applyTheme(p))
   */
  on<K extends keyof AppEventMap>(
    event: K,
    handler: Handler<AppEventMap[K]>,
  ): Unsubscribe {
    let set = this.handlers.get(event as string);
    if (!set) {
      set = new Set();
      this.handlers.set(event as string, set);
    }
    set.add(handler as Handler<never>);
    return () => this.off(event, handler);
  }

  /** 只触发一次 */
  once<K extends keyof AppEventMap>(
    event: K,
    handler: Handler<AppEventMap[K]>,
  ): Unsubscribe {
    const off = this.on(event, (payload) => {
      off();
      handler(payload);
    });
    return off;
  }

  /** 取消订阅。若 handler 未指定则清空该事件的全部订阅 */
  off<K extends keyof AppEventMap>(
    event: K,
    handler?: Handler<AppEventMap[K]>,
  ): void {
    const key = event as string;
    if (!handler) {
      this.handlers.delete(key);
      return;
    }
    this.handlers.get(key)?.delete(handler as Handler<never>);
  }

  /**
   * 广播事件。
   * 处理函数内的异常会被捕获并打印，避免一个订阅者出错拖垮整条事件链。
   */
  emit<K extends keyof AppEventMap>(event: K, payload: AppEventMap[K]): void {
    const set = this.handlers.get(event as string);
    if (!set || set.size === 0) return;

    // 拷贝一份再遍历：处理函数内可能反手 off()，直接遍历原 Set 会有遍历中修改的问题
    for (const handler of [...set]) {
      try {
        (handler as Handler<AppEventMap[K]>)(payload);
      } catch (err) {
        console.error(`[event-bus] 处理「${String(event)}」事件时出错：`, err);
      }
    }
  }

  /** 调试用：查看当前各事件的订阅数量 */
  debugStats(): Record<string, number> {
    const stats: Record<string, number> = {};
    for (const [key, set] of this.handlers) stats[key] = set.size;
    return stats;
  }
}

export const eventBus = new EventBus();
